/**
 * Stable, library-decoupled test affordance for application toasts.
 *
 * react-toastify (v9) does not let us attach a `data-testid` to a toast node
 * from a single chokepoint, so instead every toast carries a marker CLASS:
 *
 *   <div class="... Toastify__toast Toastify__toast--error  app-toast app-toast--error">
 *
 * applied container-wide via <AppToastContainer>'s `toastClassName`. These
 * class names are OURS — not react-toastify internals — so a test keyed on
 * them survives a future toast-library swap: only AppToastContainer and this
 * file would change, not the specs.
 *
 * This class is a TEST SEAM ONLY. It carries no CSS and MUST NOT be used for
 * styling, analytics, feature flags, or any production behavior — same rule as
 * the `testId?` "Test-only seam" props on Button/Modal/Table/etc.
 *
 * Why a class and not react-toastify's own affordances: v9 supplies the toast
 * `role` ("alert") via component `defaultProps`, which React 19 ignores — so no
 * role is emitted. Its `Toastify__toast--<type>` classes ARE present, but those
 * are library internals. This marker class is OURS and library-decoupled, so it
 * survives a toast upgrade/swap and specs never key on react-toastify internals.
 *
 * Single source of truth: production (`AppToastContainer`), the Jest proof
 * test, and (later) the Playwright specs all reference the helpers here so the
 * selector contract lives in exactly one place.
 */
export type ToastVariant = 'success' | 'error' | 'info' | 'warning' | 'default'

/** The variant slots we recognise; anything else normalises to `default`. */
const KNOWN_VARIANTS: readonly ToastVariant[] = [
  'success',
  'error',
  'info',
  'warning',
  'default'
]

/**
 * Stable id on the single application ToastContainer root element
 * (`<div class="Toastify" id={TOAST_CONTAINER_ID}>`). Lets a test scope a
 * query to the toast outlet; note its mere presence does NOT mean a toast is
 * showing — wait on {@link toastSelector} for that.
 */
export const TOAST_CONTAINER_ID = 'app-toast-container'

/** Marker class present on every toast root node. */
export const TOAST_HOOK_CLASS = 'app-toast'

/**
 * Full marker className for a toast of the given variant,
 * e.g. `toastVariantClass('error')` → `"app-toast app-toast--error"`.
 * Absent OR unrecognised types collapse to the `default` variant — so a future
 * react-toastify type (e.g. a distinct `loading`) can never produce a class
 * like `app-toast--loading` that `toastSelector('default')` would miss. Today
 * `toast()` and `toast.loading` both already report the `default` type.
 */
export const toastVariantClass = (type?: string): string => {
  const variant = KNOWN_VARIANTS.includes(type as ToastVariant)
    ? (type as ToastVariant)
    : 'default'
  return `${TOAST_HOOK_CLASS} ${TOAST_HOOK_CLASS}--${variant}`
}

/**
 * CSS selector a test can wait on (Playwright `page.locator(...)` or an RTL
 * container query). `toastSelector()` matches any toast; `toastSelector('error')`
 * matches only error toasts. Waiting on this — rather than sleeping or matching
 * locale-dependent body text — is the deterministic way to detect a toast.
 */
export const toastSelector = (variant?: ToastVariant): string =>
  variant ? `.${TOAST_HOOK_CLASS}--${variant}` : `.${TOAST_HOOK_CLASS}`
